/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  UtensilsCrossed, Utensils, QrCode, Trash2, Printer, Plus, Clock, AlertTriangle,
  ScanLine, Settings as SettingsIcon, RefreshCw, Pencil, X, Check, Package, Upload, Download, Tag, Flag,
  Bluetooth, ChevronDown, ShieldQuestion, Lock, History, Search, FileText,
} from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import FoodPrepScanner from "@/components/FoodPrepScanner";
import { Portal } from "@/components/Portal";
import FoodPrepReportModal from "@/pages/FoodPrepReportModal";
import {
  buildFpToken, renderFoodLabel, printFoodLabel, foodState, fmtRemaining, fmtDateTime,
} from "@/lib/foodLabel";
import { isBluetoothSupported, isPrinterConnected, connectPrinter, reconnectPrinter, printCanvas, getPrinterName, hasRememberedPrinter } from "@/lib/btPrinter";

const TABS = [
  { id: "prepare", label: "Prepare & Label", icon: UtensilsCrossed },
  { id: "scan", label: "Scanner", icon: ScanLine },
  { id: "disposal", label: "Disposal List", icon: Trash2 },
  { id: "catalog", label: "Food Catalog", icon: Package },
  { id: "history", label: "Historical Data", icon: History },
];

const UNIT_MULT = { minutes: 1, hours: 60, days: 1440 };
const STATE_PILL = { ok: "ok", expiring: "warn", expired: "err" };
const STATE_LABEL = { ok: "Good", expiring: "Expiring", expired: "Dispose now" };

const minutesToParts = (mins) => {
  if (mins % 1440 === 0) return { value: mins / 1440, unit: "days" };
  if (mins % 60 === 0) return { value: mins / 60, unit: "hours" };
  return { value: mins, unit: "minutes" };
};

const shelfText = (mins) => { const p = minutesToParts(mins); return `${p.value} ${p.unit}`; };

// Group active/disposal rows by their batch (units created together share a
// batch_id). Legacy single items fall back to their own id.
const groupByBatch = (arr) => {
  const map = new Map();
  for (const it of arr || []) {
    const key = it.batch_id || it.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return [...map.values()];
};

// Prominent quantity badge style (number of units currently in use).
const QTY_BADGE = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  minWidth: 44, height: 44, padding: "0 10px", borderRadius: 12,
  fontWeight: 800, fontSize: 20, lineHeight: 1,
  background: "var(--vb-text-2)", color: "var(--vb-bg)", flexShrink: 0,
};

export default function FoodPrep() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.id === params.get("tab"))?.id || "prepare";
  const { user } = useAuth();
  const canConfig = ["owner", "manager", "supervisor"].includes(user?.role);

  const switchTab = (id) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  return (
    <div className="vb-fade-in" data-testid="food-prep-page">
      <header className="mb-4">
        <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
          <UtensilsCrossed size={24} className="inline-block mr-2 -mt-1" />
          Food Prep
        </h1>
        <p className="text-sm opacity-70 m-0">
          Label cooked food, track shelf life, and dispose on time with QR scanning.
        </p>
      </header>

      <div className="vb-tabs mb-4" role="tablist" data-testid="food-prep-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id} role="tab" aria-selected={active}
              className={`vb-tab ${active ? "active" : ""}`}
              onClick={() => switchTab(t.id)}
              data-testid={`food-prep-tab-${t.id}`}
            >
              <Icon size={15} className="inline-block mr-1 -mt-0.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "prepare" && <PrepareTab />}
      {tab === "scan" && <ScanTab />}
      {tab === "disposal" && <DisposalTab />}
      {tab === "catalog" && <CatalogTab canConfig={canConfig} role={user?.role} />}
      {tab === "history" && <HistoryTab />}
    </div>
  );
}

// ---------------- Prepare ----------------
function PrepareTab() {
  const { activeSiteId, isAllSites, visibleSites, activeSite } = useSite();
  const { notify, confirm, prompt } = useDialog();
  const [presets, setPresets] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prepSite, setPrepSite] = useState(isAllSites ? "" : activeSiteId);
  const [form, setForm] = useState({ presetId: "", name: "", count: 1, value: 1, unit: "days" });
  const [submitting, setSubmitting] = useState(false);
  const [showShelf, setShowShelf] = useState(false);
  const [showActiveReport, setShowActiveReport] = useState(false);
  const [presetSearch, setPresetSearch] = useState("");
  const [labelData, setLabelData] = useState(null);
  const [printDims, setPrintDims] = useState({ widthMm: 80, heightMm: 50, dpi: 203 });
  const [printCfg, setPrintCfg] = useState(null);

  const siteForPrep = isAllSites ? prepSite : activeSiteId;
  const tz = activeSite?.timezone;

  const loadPresets = async () => {
    try { const { data } = await api.get("/food-prep/presets"); setPresets(data.presets || []); }
    catch (e) { console.debug(e); }
  };
  const loadItems = async () => {
    setLoading(true);
    try { const { data } = await api.get("/food-prep", { params: { status: "active" } }); setItems(data.items || []); }
    catch (e) { console.debug(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPresets(); }, []);
  useEffect(() => {
    api.get("/food-prep/print-config")
      .then(({ data }) => {
        setPrintDims({ widthMm: data.label_width_mm, heightMm: data.label_height_mm, dpi: data.label_dpi });
        setPrintCfg(data);
        // Returning user: silently re-pair with the remembered printer (persistent BLE permission).
        if (data.printer_enabled) { reconnectPrinter(data).catch(() => {}); }
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadItems(); }, [activeSiteId]);
  useEffect(() => { if (!isAllSites) setPrepSite(activeSiteId); }, [isAllSites, activeSiteId]);

  const pickPreset = (p) => {
    if (!p) { setForm((f) => ({ ...f, presetId: "" })); return; }
    const parts = minutesToParts(p.default_shelf_life_minutes);
    setForm((f) => ({ ...f, presetId: p.id, name: p.name, value: parts.value, unit: parts.unit }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!siteForPrep) { await notify({ title: "Pick a site", message: "Select which site you're preparing food for.", variant: "err" }); return; }
    if (!form.name.trim()) return;
    const shelf = Math.round(Number(form.value) * UNIT_MULT[form.unit]);
    if (!shelf || shelf < 1) { await notify({ title: "Invalid shelf life", message: "Enter a shelf life of at least 1 minute.", variant: "err" }); return; }
    const count = Math.max(1, Math.min(parseInt(form.count, 10) || 1, 200));
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(), count,
        shelf_life_minutes: shelf, site_id: siteForPrep,
        preset_id: form.presetId || null,
      };
      const { data } = await api.post("/food-prep", body);
      const siteName = visibleSites.find((s) => s.id === siteForPrep)?.name || activeSite?.name || "";
      setLabelData({ item: data.item, token: data.token, copies: data.count, siteName, justCreated: true, tz, dims: printDims, cfg: printCfg });
      loadItems();
    } catch (e) {
      await notify({ title: "Couldn't create label", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setSubmitting(false); }
  };

  const disposeItem = async (it) => {
    const remaining = it.quantity_remaining ?? 1;
    if (!(await confirm({ title: "Dispose item?", message: `Dispose all ${remaining} remaining of “${it.name}”?`, danger: true, confirmLabel: "Dispose" }))) return;
    try { await api.post(`/food-prep/${it.id}/dispose`, { all: true, reason: "manual" }); loadItems(); }
    catch (e) { await notify({ title: "Dispose failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  // "Consume" — a partial-disposal for staff to record that some units of
  // this batch were served / used up (as opposed to spoiled). Backend uses
  // the same /dispose endpoint with an explicit `qty` and a `consumed`
  // reason so downstream reporting can distinguish it from wastage.
  const consumeItem = async (it) => {
    const remaining = it.quantity_remaining ?? 1;
    if (remaining <= 0) return;
    const raw = await prompt({
      title: `Consume from “${it.name}”`,
      message: `How many units were consumed? (max ${remaining} in stock)`,
      defaultValue: String(remaining),
      inputType: "number",
      confirmLabel: "Consume",
    });
    if (raw === null || raw === undefined || raw === "") return;
    const qty = Math.floor(Number(raw));
    if (!Number.isFinite(qty) || qty < 1) {
      await notify({ title: "Invalid quantity", message: "Enter a number of at least 1.", variant: "err" });
      return;
    }
    if (qty > remaining) {
      await notify({ title: "Not enough in stock", message: `Only ${remaining} unit(s) available.`, variant: "err" });
      return;
    }
    try {
      await api.post(`/food-prep/${it.id}/dispose`, { qty, reason: "consumed" });
      loadItems();
    } catch (e) {
      await notify({ title: "Couldn't record consumption", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const toggleFlag = async (it) => {
    try { await api.post(`/food-prep/${it.id}/flag-disposal`, { flagged: !it.flagged_for_disposal }); loadItems(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const printItem = (it) => {
    const siteName = visibleSites.find((s) => s.id === it.site_id)?.name || activeSite?.name || "";
    const copies = Math.max(1, it.quantity_remaining ?? it.quantity_total ?? 1);
    setLabelData({ item: it, token: buildFpToken(it.id), copies, siteName, justCreated: false, tz, dims: printDims, cfg: printCfg });
  };

  const resetForm = () => setForm((f) => ({ ...f, name: "", count: 1, presetId: "" }));
  const closeLabel = () => { if (labelData?.justCreated) resetForm(); setLabelData(null); };
  const cancelLabel = async () => {
    const id = labelData?.item?.id;
    if (id) { try { await api.delete(`/food-prep/${id}`); } catch (e) { /* noop */ } }
    setLabelData(null);
    loadItems();
  };
  const markPrinted = async (ids) => {
    try { await api.post("/food-prep/mark-printed", { ids }); loadItems(); } catch (e) { /* noop */ }
  };

  const grouped = useMemo(() => {
    const g = { expired: [], expiring: [], ok: [] };
    items.forEach((it) => { (g[it.state] || g.ok).push(it); });
    return g;
  }, [items]);

  // When the site scope is "All sites", sub-group each state bucket by
  // site so a multi-site owner can tell at a glance which kitchen owns
  // which batch. When scoped to a single site we skip the extra header
  // to keep the list tight.
  const groupByState = (list) => {
    if (!isAllSites) return [{ site_id: null, name: null, items: list }];
    const bySite = new Map();
    list.forEach((it) => {
      const sid = it.site_id || "";
      if (!bySite.has(sid)) bySite.set(sid, []);
      bySite.get(sid).push(it);
    });
    const nameFor = (sid) => visibleSites.find((s) => s.id === sid)?.name || "Unknown site";
    return Array.from(bySite.entries())
      .map(([site_id, arr]) => ({ site_id, name: nameFor(site_id), items: arr }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  return (
    <div className="grid grid-cols-1 gap-4 fp-touch" data-testid="prepare-tab">
      <PrinterBar cfg={printCfg} />
      {/* Row 1 — New food label */}
      <form id="prepare-form" className="vb-card p-5 w-full" style={{ maxWidth: 640 }} onSubmit={submit} data-testid="prepare-form">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="vb-heading text-lg m-0">New food label</h3>
          <div className="relative" style={{ minWidth: 200 }}>
            <Search size={15} className="absolute opacity-50" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input className="vb-input" value={presetSearch} onChange={(e) => setPresetSearch(e.target.value)}
              placeholder="Search catalog…" style={{ paddingLeft: 32 }} data-testid="preset-search" />
          </div>
        </div>

        {isAllSites && (
          <label className="block mb-3 text-sm">
            <span className="block mb-1 font-medium">Site</span>
            <select className="vb-input" value={prepSite} onChange={(e) => setPrepSite(e.target.value)} data-testid="prepare-site">
              <option value="">Select a site…</option>
              {visibleSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}

        {presets.length > 0 && (() => {
          const q = presetSearch.trim().toLowerCase();
          const matches = q ? presets.filter((p) => p.name.toLowerCase().includes(q)) : presets;
          return (
            <div className="mb-3">
              <span className="block mb-1 text-sm font-medium">Quick pick from catalog</span>
              {matches.length === 0 ? (
                <div className="text-sm opacity-60" data-testid="preset-no-match">No catalog items match “{presetSearch}”. Type a name below instead.</div>
              ) : (
                <div className="fp-chips" data-testid="preset-chips">
                  {matches.map((p) => (
                    <button type="button" key={p.id}
                      className={`fp-chip ${form.presetId === p.id ? "active" : ""}`}
                      onClick={() => pickPreset(p)} data-testid={`preset-chip-${p.id}`}>
                      <span className="fp-chip-name">{p.name}</span>
                      <span className="fp-chip-sub">{shelfText(p.default_shelf_life_minutes)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <label className="block mb-3 text-sm">
          <span className="block mb-1 font-medium">Food name</span>
          <input className="vb-input" value={form.name} required
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, presetId: "" }))}
            placeholder="e.g. Chicken Stock" data-testid="prepare-name" />
        </label>

        <label className="block mb-3 text-sm">
          <span className="block mb-1 font-medium">Quantity <span className="opacity-50">(number of labels / units)</span></span>
          <input className="vb-input fp-qty" type="number" min="1" max="200" inputMode="numeric" value={form.count}
            onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
            onFocus={(e) => { setForm((f) => ({ ...f, count: "" })); e.target.select(); }}
            onBlur={() => setForm((f) => ({ ...f, count: f.count === "" ? 1 : f.count }))}
            placeholder="1" data-testid="prepare-qty" />
          <span className="text-xs opacity-60">Each unit prints its own label with a unique QR so you can dispose one at a time.</span>
        </label>

        <button type="button" className="fp-shelf-toggle vb-btn ghost sm mb-3" onClick={() => setShowShelf((s) => !s)} data-testid="prepare-shelf-toggle">
          <Clock size={14} /> {showShelf ? "Hide shelf life" : `Adjust shelf life · ${form.value} ${form.unit}`}
        </button>
        <div className={`mb-3 fp-shelf-block ${showShelf ? "" : "fp-collapsed"}`}>
          <span className="block mb-1 text-sm font-medium">Shelf life</span>
          <div className="flex gap-2">
            <input className="vb-input" type="number" min="1" inputMode="numeric" value={form.value} style={{ maxWidth: 110 }}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} data-testid="prepare-shelf-value" />
            <select className="vb-input" value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="prepare-shelf-unit">
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
          </div>
        </div>

        <button className="vb-btn w-full fp-inline-submit" disabled={submitting} data-testid="prepare-submit">
          {submitting ? "Creating…" : <><QrCode size={16} /> Create & print label</>}
        </button>
      </form>

      {/* Row 2 — Active inventory */}
      <div className="w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="vb-heading text-lg m-0">Active inventory ({items.length})</h3>
          <div className="flex items-center gap-2">
            <button
              className="vb-btn ghost xs"
              onClick={() => setShowActiveReport(true)}
              disabled={items.length === 0}
              data-testid="prepare-generate-report"
            >
              <FileText size={13} /> Generate report
            </button>
            <button className="vb-btn ghost xs" onClick={loadItems} data-testid="prepare-refresh"><RefreshCw size={13} /> Refresh</button>
          </div>
        </div>
        {loading ? (
          <div className="vb-card p-5 text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="vb-card p-5 text-sm opacity-60" data-testid="active-empty">No active food prep yet. Create a label to get started.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {["expired", "expiring", "ok"].map((st) => {
              if (grouped[st].length === 0) return null;
              const totalUnits = grouped[st].reduce((s, it) => s + (it.quantity_remaining ?? 1), 0);
              return (
                <div key={st} className="vb-card p-5" data-testid={`inventory-group-${st}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`vb-pill ${STATE_PILL[st]}`}>{STATE_LABEL[st]}</span>
                    <span className="text-sm opacity-60">{grouped[st].length} batch{grouped[st].length === 1 ? "" : "es"} · {totalUnits} unit{totalUnits === 1 ? "" : "s"}</span>
                  </div>
                  <div>
                    {groupByState(grouped[st]).map((siteGroup, gi) => (
                      <div key={siteGroup.site_id || "single"}
                        style={{ marginTop: gi === 0 ? 0 : 12 }}
                        data-testid={`inventory-site-group-${st}-${siteGroup.site_id || "single"}`}>
                        {siteGroup.name && (
                          <div className="text-xs font-medium opacity-70 mb-1 flex items-center gap-1.5"
                            style={{ letterSpacing: 0.2 }}>
                            <span className="vb-pill" style={{ padding: "1px 8px", fontSize: 10, background: "var(--vb-bg-2, #faf6f1)" }}>
                              {siteGroup.name}
                            </span>
                            <span className="opacity-50">· {siteGroup.items.length} batch{siteGroup.items.length === 1 ? "" : "es"}</span>
                          </div>
                        )}
                        {siteGroup.items.map((it, idx) => (
                          <InventoryRow
                            key={it.id} item={it} tz={tz} last={idx === siteGroup.items.length - 1}
                            onDispose={() => disposeItem(it)}
                            onConsume={() => consumeItem(it)}
                            onToggleFlag={() => toggleFlag(it)}
                            onPrint={() => printItem(it)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {labelData && (
        <LabelModal
          {...labelData}
          onClose={closeLabel}
          onCancel={labelData.justCreated ? cancelLabel : null}
          onPrinted={markPrinted}
        />
      )}
      {showActiveReport && (
        <FoodPrepReportModal
          variant="active"
          onClose={() => setShowActiveReport(false)}
        />
      )}
    </div>
  );
}

// ---------------- Active inventory batch row ----------------
function InventoryRow({ item: it, tz, last, onDispose, onConsume, onToggleFlag, onPrint }) {
  const remaining = it.quantity_remaining ?? 1;
  const total = it.quantity_total ?? remaining;
  const anyFlagged = it.flagged_for_disposal;
  const printed = !!it.printed_at;
  const key = it.id;
  return (
    <div className="flex items-center justify-between gap-3 py-3"
      style={{ borderBottom: last ? "none" : "1px solid var(--vb-border)" }}
      data-testid={`active-batch-${key}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span style={QTY_BADGE} data-testid={`active-qty-${key}`} title={`${remaining} of ${total} remaining`}>×{remaining}</span>
        <div className="min-w-0">
          <div className="font-medium truncate flex items-center gap-1.5 flex-wrap">
            {it.name}
            {total > remaining && <span className="text-xs opacity-50">of {total}</span>}
            {anyFlagged && <span className="vb-pill warn" style={{ padding: "1px 6px", fontSize: 10 }} data-testid={`active-flag-pill-${key}`}><Flag size={9} /> Marked</span>}
            {printed
              ? <span className="vb-pill ok" style={{ padding: "1px 6px", fontSize: 10 }} data-testid={`active-print-pill-${key}`}><Check size={9} /> Printed</span>
              : <span className="vb-pill" style={{ padding: "1px 6px", fontSize: 10, background: "#e7e5e4", color: "#57534e" }} data-testid={`active-print-pill-${key}`}>Not printed</span>}
          </div>
          <div className="text-xs opacity-70 flex items-center gap-1 mt-0.5">
            <Clock size={11} /> Use by {fmtDateTime(it.use_by, tz)} · {fmtRemaining(it.minutes_remaining)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button className="vb-btn ghost xs" title="Consume — deduct served / used units"
          onClick={onConsume} data-testid={`active-consume-${key}`}><Utensils size={13} /></button>
        <button className={`vb-btn xs ${anyFlagged ? "warn" : "ghost"}`}
          title={anyFlagged ? "Remove from disposal list" : "Add to disposal list"}
          onClick={onToggleFlag} data-testid={`active-flag-${key}`}><Flag size={13} /></button>
        <button className="vb-btn ghost xs" title={remaining > 1 ? `Print ${remaining} labels` : "Print label"}
          onClick={onPrint} data-testid={`active-print-${key}`}><Printer size={13} /></button>
        <button className="vb-btn ghost xs" title="Dispose all remaining" onClick={onDispose}
          data-testid={`active-dispose-${key}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

// ---------------- Printer status chip + one-time setup helper ----------------
function PrinterBar({ cfg }) {
  const { notify } = useDialog();
  const enabled = cfg?.printer_enabled;
  const supported = isBluetoothSupported();
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | disconnected
  const [connecting, setConnecting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("vb_bt_setup_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!enabled || !supported) return;
    let alive = true;
    (async () => {
      if (isPrinterConnected()) { setStatus("connected"); return; }
      const ok = await reconnectPrinter(cfg).catch(() => false);
      if (alive) setStatus(ok ? "connected" : "disconnected");
    })();
    const iv = setInterval(() => {
      if (!alive) return;
      setStatus((s) => (isPrinterConnected() ? "connected" : (s === "connecting" ? "connecting" : "disconnected")));
    }, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [enabled, supported, cfg]);

  const connect = async () => {
    setConnecting(true); setStatus("connecting");
    try {
      if (!isPrinterConnected()) {
        const ok = await reconnectPrinter(cfg);
        if (!ok) await connectPrinter(cfg);
      }
      setStatus("connected");
      await notify({ title: `Printer connected${getPrinterName() ? ` · ${getPrinterName()}` : ""}`, variant: "ok" });
    } catch (e) {
      setStatus("disconnected");
      await notify({ title: "Couldn't connect", message: e?.message || "Make sure the printer is on and nearby.", variant: "err" });
    } finally { setConnecting(false); }
  };

  const openHelp = () => { setShowHelp(true); setDismissed(false); };
  const dismissHelp = () => {
    setShowHelp(false); setDismissed(true);
    try { localStorage.setItem("vb_bt_setup_dismissed", "1"); } catch { /* ignore */ }
  };

  if (!enabled) return null; // BLE printing off → no chip
  if (!supported) {
    return (
      <div className="vb-card p-3 flex items-center gap-2 text-sm" data-testid="printer-bar-unsupported" style={{ background: "var(--vb-bg)" }}>
        <Printer size={15} className="opacity-70 shrink-0" />
        <span className="opacity-75">This device can't print directly over Bluetooth — labels open in the print preview instead. For one-tap printing, use Chrome on an Android device.</span>
      </div>
    );
  }

  const connected = status === "connected";
  const firstTime = !hasRememberedPrinter();
  const showSetup = (firstTime || showHelp) && !dismissed;

  return (
    <div className="grid gap-2" data-testid="printer-bar">
      <div className="vb-card p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center rounded-full shrink-0" style={{ width: 26, height: 26, background: connected ? "#dcfce7" : "#fef3c7" }}>
            <Bluetooth size={15} style={{ color: connected ? "#16a34a" : "#b45309" }} />
          </span>
          <div className="leading-tight">
            <div className="text-sm font-medium" data-testid="printer-status-label">
              {connected ? `Printer connected${getPrinterName() ? ` · ${getPrinterName()}` : ""}` : (status === "connecting" ? "Connecting…" : "Printer not connected")}
            </div>
            <div className="text-xs opacity-60">
              {connected ? "Labels print straight to your Bluetooth printer." : "Tap Connect to link your Bluetooth label printer."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!connected && (
            <button className="vb-btn sm" disabled={connecting} onClick={connect} data-testid="printer-connect-btn">
              {connecting ? <RefreshCw size={14} className="animate-spin" /> : <Bluetooth size={14} />} {connecting ? "Connecting…" : "Connect"}
            </button>
          )}
          {connected && <Check size={16} style={{ color: "#16a34a" }} data-testid="printer-connected-check" />}
          <button className="vb-btn ghost xs" onClick={openHelp} data-testid="printer-help-toggle" aria-label="Printer setup help">
            <ShieldQuestion size={15} />
          </button>
        </div>
      </div>

      {showSetup && (
        <div className="vb-card p-4" data-testid="printer-setup-helper" style={{ borderLeft: "3px solid #b45309" }}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="vb-heading text-base m-0 flex items-center gap-2"><Printer size={16} /> One-time printer setup</h4>
            <button className="vb-btn ghost xs" onClick={dismissHelp} data-testid="printer-setup-dismiss" aria-label="Dismiss"><X size={14} /></button>
          </div>
          <ol className="text-sm m-0 pl-5" style={{ listStyle: "decimal" }}>
            <li className="mb-2">Turn on your <strong>JiaBo</strong> label printer and keep it nearby.</li>
            <li className="mb-2">Tap <strong>Connect</strong> above (or <strong>Print</strong> on a label) → pick your printer from the Bluetooth list.</li>
            <li className="mb-2"><strong>To remember it permanently:</strong> tap the <strong>lock icon</strong> in Chrome's address bar → <strong>Permissions → Bluetooth devices</strong> → set your printer to <strong>“Allow”</strong>.</li>
            <li>Done — it now reconnects automatically every time you open Food Prep, with no pop-up.</li>
          </ol>
          <p className="text-xs opacity-60 mt-3 mb-0">Step 3 is what lets the app remember your printer across sessions. It's a one-time step per phone/tablet.</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Label modal (supports a batch of unique-QR labels) ----------------
function LabelModal({ item, token, copies = 1, siteName, justCreated, tz, dims, cfg, onClose, onCancel, onPrinted }) {
  const canvasRef = useRef(null);
  const { notify } = useDialog();
  const [btBusy, setBtBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const d = dims || { widthMm: 80, heightMm: 50, dpi: 203 };
  const aspect = d.heightMm / d.widthMm;
  const btEnabled = cfg?.printer_enabled;
  const n = Math.max(1, copies || 1);

  useEffect(() => {
    if (!canvasRef.current || !item) return;
    renderFoodLabel(canvasRef.current, {
      name: item.name,
      useByText: fmtDateTime(item.use_by, tz),
      prepText: `Prep: ${fmtDateTime(item.prepared_at, tz)}`,
      byText: `By: ${item.created_by_name || "—"}`,
      siteText: siteName ? `Site: ${siteName}` : "",
      qtyText: `Qty: ${n}`,
      token,
    }, d);
  }, [item, token, siteName, n]);

  const systemPrint = () => {
    printFoodLabel(canvasRef.current, item?.name || "Food label", d, n);
    onPrinted?.([item.id]);
  };

  const btPrint = async () => {
    if (!isBluetoothSupported()) {
      // Unsupported browser (iOS/desktop) — silently fall back to the system print path.
      systemPrint();
      return;
    }
    setBtBusy(true);
    try {
      if (!isPrinterConnected()) {
        const ok = await reconnectPrinter(cfg);
        if (!ok) await connectPrinter(cfg);
      }
      await printCanvas(canvasRef.current, cfg, n);
      onPrinted?.([item.id]);
      await notify({ title: `Sent ${n} label${n === 1 ? "" : "s"} to printer`, variant: "ok" });
    } catch (e) {
      await notify({ title: "Bluetooth print failed", message: e?.message || "Could not print.", variant: "err" });
    } finally { setBtBusy(false); }
  };

  const doCancel = async () => {
    setCancelling(true);
    try { await onCancel?.(); } finally { setCancelling(false); }
  };

  if (!item) return null;

  return (
    <Portal>
    <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="label-modal">
      <div className="vb-modal" style={{ maxWidth: 420 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="m-0 text-lg">Food label{n > 1 ? ` · ${n} copies` : ""}</h2>
          <button className="vb-btn ghost xs" onClick={onClose} data-testid="label-close"><X size={15} /></button>
        </div>
        <div className="flex justify-center mb-2" style={{ background: "var(--vb-bg)" }}>
          <canvas ref={canvasRef}
            style={{ width: 320, height: Math.round(320 * aspect), border: "1px solid var(--vb-border)", borderRadius: 8 }} />
        </div>
        {n > 1 && <p className="text-xs opacity-60 text-center mb-2" data-testid="label-copies">Prints {n} identical labels (one shared QR — scanning deducts one unit at a time).</p>}
        <div className="flex gap-2 justify-end flex-wrap">
          {onCancel ? (
            <button className="vb-btn danger" disabled={cancelling || btBusy} onClick={doCancel} data-testid="label-cancel">
              <Trash2 size={15} /> {cancelling ? "Cancelling…" : "Cancel"}
            </button>
          ) : (
            <button className="vb-btn ghost" onClick={onClose}>Done</button>
          )}
          {btEnabled ? (
            <button className="vb-btn" disabled={btBusy} onClick={btPrint} data-testid="label-print">
              <Bluetooth size={16} /> {btBusy ? "Printing…" : (n > 1 ? `Print ${n}` : "Print")}
            </button>
          ) : (
            <button className="vb-btn" onClick={systemPrint} data-testid="label-print">
              <Printer size={16} /> {n > 1 ? `Print ${n}` : "Print"}
            </button>
          )}
        </div>
        {onCancel && (
          <button className="vb-btn ghost w-full mt-2" onClick={onClose} data-testid="label-done">Done (keep without printing)</button>
        )}
        <p className="text-xs opacity-50 mt-2 text-center">
          {onCancel ? "Cancel removes this just-created batch so you can adjust. " : ""}
          {btEnabled
            ? `Bluetooth: Android/Chrome only. Browser “Print” caps at 500 copies.`
            : `Prints at ${d.widthMm} × ${d.heightMm} mm via your device's printer (thermal / AirPrint).`}
        </p>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Scanner tab ----------------
function ScanTab() {
  return (
    <div className="vb-card p-5 w-full">
      <FoodPrepScanner />
    </div>
  );
}

// ---------------- Disposal list ----------------
function DisposalTab() {
  const { activeSiteId, activeSite } = useSite();
  const { notify, confirm } = useDialog();
  const [data, setData] = useState({ flagged: [], expired: [], expiring: [], consumed_recent: [], disposed_recent: [] });
  const [loading, setLoading] = useState(true);
  const [showDisposed, setShowDisposed] = useState(false);
  const [showConsumed, setShowConsumed] = useState(false);
  const tz = activeSite?.timezone;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/food-prep/disposal-list"); setData(data); }
    catch (e) { console.debug(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeSiteId]);

  const disposeMany = async (ids, title) => {
    if (!ids.length) return;
    if (!(await confirm({ title, message: `Dispose ${ids.length} item(s)?`, danger: true, confirmLabel: "Dispose all" }))) return;
    try { await api.post("/food-prep/dispose-bulk", { ids, reason: "eod_disposal" }); load(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const disposeItem = async (it) => {
    try { await api.post(`/food-prep/${it.id}/dispose`, { all: true, reason: "manual" }); load(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const unflagItem = async (it) => {
    try { await api.post(`/food-prep/${it.id}/flag-disposal`, { flagged: false }); load(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  if (loading) return <div className="vb-card p-5 text-sm opacity-60">Loading…</div>;

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="disposal-tab">
      <div className="vb-card p-5" data-testid="disposal-flagged-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="vb-heading text-lg m-0 flex items-center gap-2" style={{ color: "#b45309" }}>
            <Flag size={18} /> Marked for disposal ({data.flagged.length})
          </h3>
          {data.flagged.length > 0 && (
            <button className="vb-btn danger sm" onClick={() => disposeMany(data.flagged.map((i) => i.id), "Dispose all marked?")} data-testid="dispose-all-flagged">
              <Trash2 size={14} /> Dispose all marked
            </button>
          )}
        </div>
        {data.flagged.length === 0 ? (
          <div className="text-sm opacity-60">No items marked for disposal. Use the flag button in Active Inventory.</div>
        ) : data.flagged.map((it, idx) => (
          <DisposeRow key={it.id} item={it} tz={tz} last={idx === data.flagged.length - 1}
            onDispose={() => disposeItem(it)} onUnflag={() => unflagItem(it)} />
        ))}
      </div>

      <div className="vb-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="vb-heading text-lg m-0 flex items-center gap-2" style={{ color: "var(--vb-error)" }}>
            <AlertTriangle size={18} /> Dispose now ({data.expired.length})
          </h3>
          {data.expired.length > 0 && (
            <button className="vb-btn danger sm" onClick={() => disposeMany(data.expired.map((i) => i.id), "Dispose all overdue?")} data-testid="dispose-all">
              <Trash2 size={14} /> Dispose all overdue
            </button>
          )}
        </div>
        {data.expired.length === 0 ? (
          <div className="text-sm opacity-60 flex items-center gap-2"><Check size={15} /> Nothing overdue — kitchen is clear.</div>
        ) : data.expired.map((it, idx) => (
          <DisposeRow key={it.id} item={it} tz={tz} last={idx === data.expired.length - 1} onDispose={() => disposeItem(it)} />
        ))}
      </div>

      <div className="vb-card p-5">
        <h3 className="vb-heading text-lg mt-0 mb-3 flex items-center gap-2" style={{ color: "#9a3412" }}>
          <Clock size={18} /> Expiring soon ({data.expiring.length})
        </h3>
        {data.expiring.length === 0 ? (
          <div className="text-sm opacity-60">Nothing expiring within the alert window.</div>
        ) : data.expiring.map((it, idx) => (
          <DisposeRow key={it.id} item={it} tz={tz} last={idx === data.expiring.length - 1} onDispose={() => disposeItem(it)} />
        ))}
      </div>

      <div className="vb-card p-5" data-testid="disposal-consumed-card">
        <button type="button" className="flex items-center justify-between w-full text-left"
          onClick={() => setShowConsumed((s) => !s)} data-testid="consumed-toggle">
          <h3 className="vb-heading text-lg m-0 flex items-center gap-2" style={{ color: "#166534" }}>
            <Utensils size={18} /> Consumed this week ({(data.consumed_recent || []).length})
          </h3>
          <ChevronDown size={18} style={{ transform: showConsumed ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
        </button>
        {showConsumed && (
          <>
            <p className="text-xs opacity-60 mt-2 mb-3">
              Batches recorded as served / used up (via the Consume button on Active Inventory) in the last 7 days.
              These do NOT count toward wastage.
            </p>
            {(data.consumed_recent || []).length === 0 ? (
              <div className="text-sm opacity-60">Nothing consumed in the last 7 days.</div>
            ) : data.consumed_recent.map((it, idx) => (
              <DisposedRow key={it.id} it={it} tz={tz} last={idx === data.consumed_recent.length - 1} />
            ))}
          </>
        )}
      </div>

      <div className="vb-card p-5" data-testid="disposal-disposed-card">
        <button type="button" className="flex items-center justify-between w-full text-left" onClick={() => setShowDisposed((s) => !s)} data-testid="disposed-toggle">
          <h3 className="vb-heading text-lg m-0 flex items-center gap-2" style={{ color: "#6d28d9" }}>
            <Check size={18} /> Disposed this week ({data.disposed_recent.length})
          </h3>
          <ChevronDown size={18} style={{ transform: showDisposed ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
        </button>
        {showDisposed && (
          <>
            <p className="text-xs opacity-60 mt-2 mb-3">Items disposed in the last 7 days. Older records are archived and kept per your retention setting.</p>
            {data.disposed_recent.length === 0 ? (
              <div className="text-sm opacity-60">Nothing disposed in the last 7 days.</div>
            ) : data.disposed_recent.map((it, idx) => (
              <DisposedRow key={it.id} it={it} tz={tz} last={idx === data.disposed_recent.length - 1} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function DisposeRow({ item: it, tz, last, onDispose, onUnflag }) {
  const remaining = it.quantity_remaining ?? 1;
  const key = it.id;
  return (
    <div className="flex items-center justify-between gap-3 py-3" data-testid={`disposal-row-${key}`}
      style={{ borderBottom: last ? "none" : "1px solid var(--vb-border)" }}>
      <div className="flex items-center gap-3 min-w-0">
        <span style={QTY_BADGE} data-testid={`disposal-qty-${key}`}>×{remaining}</span>
        <div className="min-w-0">
          <div className="font-medium truncate">{it.name}</div>
          <div className="text-xs opacity-70 mt-0.5">Use by {fmtDateTime(it.use_by, tz)} · {fmtRemaining(it.minutes_remaining)}{it.flagged_by_name ? ` · marked by ${it.flagged_by_name}` : ""}</div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onUnflag && <button className="vb-btn ghost xs" onClick={onUnflag} title="Remove from disposal list" data-testid={`disposal-unflag-${key}`}><X size={13} /></button>}
        <button className="vb-btn ghost xs" onClick={onDispose} data-testid={`disposal-dispose-${key}`}><Trash2 size={13} /> Dispose all</button>
      </div>
    </div>
  );
}

function DisposedRow({ it, tz, last }) {
  // A "Consumed" event lives in the same disposal_events table as a
  // wastage event — only the reason string differentiates the two.
  // NOTE: /food-prep/disposal-list emits the field as `reason` while
  // /food-prep/history emits `disposal_reason` — accept both so this
  // row works on either endpoint without a backend rename.
  const reasonRaw = it.disposal_reason || it.reason || "";
  const isConsumed = reasonRaw.toLowerCase() === "consumed";
  const verb = isConsumed ? "Consumed" : "Disposed";
  return (
    <div className="flex items-center justify-between gap-3 py-3" data-testid={`disposed-row-${it.id}`}
      style={{ borderBottom: last ? "none" : "1px solid var(--vb-border)" }}>
      <div className="min-w-0">
        <div className="font-medium truncate">{it.name}{it.quantity ? <span className="opacity-50 font-normal"> · {it.quantity}</span> : null}</div>
        <div className="text-xs opacity-70 mt-0.5">
          {verb} {fmtDateTime(it.disposed_at, tz)}{it.disposed_by_name ? ` · by ${it.disposed_by_name}` : ""}{!isConsumed && reasonRaw ? ` · ${reasonRaw}` : ""}
        </div>
      </div>
    </div>
  );
}

// ---------------- Historical Data ----------------
const REASON_LABEL = {
  scanned_disposal: "Scanned (dispose mode)",
  marked_disposal: "Marked for disposal",
  eod_disposal: "End-of-day disposal",
  manual: "Manual",
  consumed: "Consumed",
};
const fmtReason = (r) => (r ? (REASON_LABEL[r] || r) : "—");

const monthOptions = (count = 18) => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < count; i++) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    out.push({ ym, label });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

function HistoryTab() {
  const { activeSiteId, isAllSites, visibleSites, activeSite } = useSite();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const tz = activeSite?.timezone;
  const months = useMemo(() => monthOptions(18), []);
  const siteName = (id) => visibleSites.find((s) => s.id === id)?.name || "—";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/food-prep/history", { params: { month } }); setData(data); }
    catch (e) { console.debug(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month, activeSiteId]);

  const t = data.totals || {};
  const monthLabel = months.find((m) => m.ym === month)?.label || month;

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="history-tab">
      <div className="vb-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="vb-heading text-lg m-0 flex items-center gap-2"><History size={18} /> Historical Data</h3>
            <p className="text-xs opacity-60 m-0 mt-1">Every food-prep transaction prepared or disposed in the selected month — kept for audit.</p>
          </div>
          <label className="text-sm flex items-center gap-2">
            <span className="font-medium">Month</span>
            <select className="vb-input" value={month} onChange={(e) => setMonth(e.target.value)} style={{ minWidth: 180 }} data-testid="history-month">
              {months.map((m) => <option key={m.ym} value={m.ym}>{m.label}</option>)}
            </select>
          </label>
        </div>

        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }} data-testid="history-totals">
          <StatTile label="Batches prepared" value={t.prepared_batches ?? 0} />
          <StatTile label="Units prepared" value={t.prepared_units ?? 0} />
          <StatTile label="Units disposed" value={t.disposed_units ?? 0} />
          <StatTile label="On-time" value={t.on_time_units ?? 0} tone="ok" />
          <StatTile label="Overdue" value={t.late_units ?? 0} tone={t.late_units ? "err" : undefined} />
          <StatTile label="Still active" value={t.active_units ?? 0} />
        </div>

        {/* Consumption vs Wastage — split by dispose reason so managers can
            see how much of what they prepped got served/used vs binned. */}
        <ConsumptionChart totals={t} month={month} onGenerateReport={() => setShowMonthlyReport(true)} />
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : (data.items || []).length === 0 ? (
          <div className="text-sm opacity-60" data-testid="history-empty">No food-prep transactions in {monthLabel}.</div>
        ) : (
          <div className="vb-access-scroll">
            <table className="vb-table" data-testid="history-table">
              <thead>
                <tr>
                  <th>Item</th>
                  {isAllSites && <th>Site</th>}
                  <th>Prepared</th>
                  <th>Use by</th>
                  <th>Status</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const isConsumed = (it.disposal_reason || "").toLowerCase() === "consumed";
                  const isDisposed = it.status === "disposed";
                  return (
                    <tr key={it.id} data-testid={`history-row-${it.id}`}>
                      <td>
                        <div className="font-medium">{it.name}</div>
                        {it.unit_total > 1 && <div className="text-xs opacity-60">Unit {it.unit_index}/{it.unit_total}</div>}
                      </td>
                      {isAllSites && <td className="text-sm">{siteName(it.site_id)}</td>}
                      <td className="text-sm whitespace-nowrap">{fmtDateTime(it.prepared_at, tz)}<div className="text-xs opacity-60">by {it.created_by_name || "—"}</div></td>
                      <td className="text-sm whitespace-nowrap">{fmtDateTime(it.use_by, tz)}</td>
                      <td>
                        {/* Status pill differentiates Consumed (positive)
                            from plain Disposed (wastage / EOD / spoilage)
                            using disposal_reason. Both share status=disposed
                            in the DB, so the reason is the only signal. */}
                        {isDisposed && isConsumed
                          ? <span className="vb-pill ok" data-testid={`history-status-${it.id}`}>Consumed</span>
                          : isDisposed
                            ? <span className="vb-pill" style={{ background: "#ede9fe", color: "#6d28d9" }} data-testid={`history-status-${it.id}`}>Disposed</span>
                            : <span className={`vb-pill ${STATE_PILL[it.state] || "ok"}`} data-testid={`history-status-${it.id}`}>{STATE_LABEL[it.state] || "Active"}</span>}
                        {isDisposed && it.disposed_at && (
                          <div className="text-xs opacity-60 mt-1">
                            {fmtDateTime(it.disposed_at, tz)}
                            {it.disposed_by_name ? ` · by ${it.disposed_by_name}` : ""}
                            {!isConsumed && it.disposal_reason ? ` · ${fmtReason(it.disposal_reason)}` : ""}
                          </div>
                        )}
                      </td>
                      <td>
                        {isDisposed && isConsumed
                          ? <span className="vb-pill ok" data-testid={`history-outcome-${it.id}`}>Consumed</span>
                          : isDisposed
                            ? (it.late
                                ? <span className="vb-pill err" data-testid={`history-outcome-${it.id}`}>Overdue</span>
                                : <span className="vb-pill ok" data-testid={`history-outcome-${it.id}`}>On-time</span>)
                            : <span className="text-xs opacity-60" data-testid={`history-outcome-${it.id}`}>{fmtRemaining(it.minutes_remaining)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showMonthlyReport && (
        <FoodPrepReportModal
          variant="monthly"
          month={month}
          onClose={() => setShowMonthlyReport(false)}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const color = tone === "ok" ? "#2e7d32" : tone === "err" ? "var(--vb-error)" : "var(--vb-text-2)";
  return (
    <div className="rounded-xl p-3 text-center" style={{ background: "var(--vb-bg)", border: "1px solid var(--vb-border)" }}>
      <div className="text-2xl font-extrabold leading-none" style={{ color }}>{value}</div>
      <div className="text-xs opacity-60 mt-1">{label}</div>
    </div>
  );
}

/**
 * Consumption vs Wastage card — shows a stacked horizontal bar for the
 * month with %-share on each side, plus tiny labelled counts. Rendered
 * empty state when there's zero out-flow to avoid dividing by 0.
 */
function ConsumptionChart({ totals, month, onGenerateReport }) {
  const consumed = Number(totals?.consumed_units || 0);
  const wastage = Number(totals?.wastage_units || 0);
  const total = consumed + wastage;
  const cPct = total ? Number(totals?.consumed_pct ?? (consumed / total * 100)) : 0;
  const wPct = total ? Number(totals?.wastage_pct ?? (wastage / total * 100)) : 0;
  return (
    <div className="rounded-xl p-4 mb-4" data-testid="consumption-chart"
      style={{ background: "var(--vb-bg)", border: "1px solid var(--vb-border)" }}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Utensils size={14} className="opacity-70" />
          <span className="font-medium">Consumption vs Wastage</span>
          <span className="text-xs opacity-60">({total} unit{total === 1 ? "" : "s"} disposed)</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {total > 0 && (
            <div className="text-xs flex items-center gap-3">
              <span data-testid="consumption-pct" style={{ color: "#166534" }}>
                <b>{cPct.toFixed(1)}%</b> consumed
              </span>
              <span data-testid="wastage-pct" style={{ color: "#9a3412" }}>
                <b>{wPct.toFixed(1)}%</b> wasted
              </span>
            </div>
          )}
          <button
            className="vb-btn ghost xs"
            onClick={onGenerateReport}
            data-testid="consumption-generate-report"
            style={{ padding: "3px 10px", fontSize: 11 }}
          >
            <FileText size={12} /> Generate report
          </button>
        </div>
      </div>
      {total === 0 ? (
        <div className="text-xs opacity-60 py-2" data-testid="consumption-empty">
          No disposals recorded this month yet.
        </div>
      ) : (
        <div className="flex h-3 rounded-full overflow-hidden" style={{ background: "var(--vb-border)" }}
          role="img" aria-label={`${cPct.toFixed(0)}% consumed, ${wPct.toFixed(0)}% wasted`}>
          {cPct > 0 && (
            <div title={`Consumed: ${consumed} unit(s)`} data-testid="consumption-bar-consumed"
              style={{ width: `${cPct}%`, background: "#4ade80", transition: "width 220ms ease" }} />
          )}
          {wPct > 0 && (
            <div title={`Wastage: ${wastage} unit(s)`} data-testid="consumption-bar-wastage"
              style={{ width: `${wPct}%`, background: "#f97316", transition: "width 220ms ease" }} />
          )}
        </div>
      )}
      {total > 0 && (
        <div className="text-[11px] opacity-60 mt-2">
          {consumed} unit{consumed === 1 ? "" : "s"} served / used · {wastage} unit{wastage === 1 ? "" : "s"} discarded
        </div>
      )}
    </div>
  );
}


// ---------------- Catalog (presets + settings) ----------------
function CatalogTab({ canConfig, role }) {
  const { notify, confirm } = useDialog();
  const [presets, setPresets] = useState([]);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);  // preset being edited / {} for new
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const canSettings = ["owner", "manager"].includes(role);

  const load = async () => {
    try {
      const [p, s, c] = await Promise.all([
        api.get("/food-prep/presets"),
        api.get("/food-prep/settings"),
        api.get("/food-prep/categories"),
      ]);
      setPresets(p.data.presets || []); setSettings(s.data); setCategories(c.data.categories || []);
    } catch (e) { console.debug(e); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!(await confirm({ title: "Delete preset?", message: `Remove “${p.name}” from the catalog?`, danger: true, confirmLabel: "Delete" }))) return;
    try { await api.delete(`/food-prep/presets/${p.id}`); load(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/food-prep/presets/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "food_catalog.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { await notify({ title: "Export failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/food-prep/presets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const lines = [`Added ${data.created} preset(s).`];
      if (data.categories_created?.length) lines.push(`New categories: ${data.categories_created.join(", ")}.`);
      if (data.duplicates?.length) lines.push(`Skipped ${data.duplicates.length} duplicate(s): ${data.duplicates.slice(0, 8).join(", ")}${data.duplicates.length > 8 ? "…" : ""}.`);
      if (data.errors?.length) lines.push(`${data.errors.length} row error(s): ` + data.errors.slice(0, 6).map((er) => `row ${er.row} — ${er.message}`).join("; ") + (data.errors.length > 6 ? "…" : ""));
      await notify({ title: "Import complete", message: lines.join("\n"), variant: data.errors?.length ? "warn" : "ok" });
      load();
    } catch (e) {
      await notify({ title: "Import failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setImporting(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="catalog-tab">
      <div className="vb-card p-5">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h3 className="vb-heading text-lg m-0">Food Catalog ({presets.length})</h3>
          {canConfig && (
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onImportFile} data-testid="catalog-import-input" />
              <button className="vb-btn ghost sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="catalog-import">
                <Upload size={14} /> {importing ? "Importing…" : "Import Excel"}
              </button>
              <button className="vb-btn ghost sm" onClick={exportXlsx} data-testid="catalog-export"><Download size={14} /> Export</button>
              <button className="vb-btn sm" onClick={() => setEditing({})} data-testid="preset-add"><Plus size={14} /> Add preset</button>
            </div>
          )}
        </div>
        <p className="text-xs opacity-60 mb-3">Presets let chefs label common items in two taps. Use Export as a template — the file has a <strong>Food Catalog</strong> tab (with Category &amp; unit drop-downs) and a <strong>Categories</strong> tab where you can add your own categories. Then Import for bulk uploads (duplicates and invalid rows are reported and skipped).</p>
        {presets.length === 0 ? (
          <div className="text-sm opacity-60">No presets yet.</div>
        ) : (
          <table className="vb-table">
            <thead><tr><th>Name</th><th>Category</th><th>Shelf life</th>{canConfig && <th></th>}</tr></thead>
            <tbody>
              {presets.map((p) => {
                const parts = minutesToParts(p.default_shelf_life_minutes);
                return (
                  <tr key={p.id} data-testid={`preset-row-${p.id}`}>
                    <td>{p.name}</td>
                    <td>{p.category || <span className="opacity-40">—</span>}</td>
                    <td>{parts.value} {parts.unit}</td>
                    {canConfig && (
                      <td className="text-right whitespace-nowrap">
                        <button className="vb-btn ghost xs" onClick={() => setEditing(p)} data-testid={`preset-edit-${p.id}`}><Pencil size={12} /></button>
                        <button className="vb-btn ghost xs" onClick={() => remove(p)} data-testid={`preset-delete-${p.id}`}><Trash2 size={12} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(canConfig || canSettings) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {canConfig && <CategoriesCard categories={categories} onChanged={load} />}
          {canSettings && settings && <SettingsCard settings={settings} onSaved={load} />}
        </div>
      )}

      {editing && canConfig && (
        <PresetModal preset={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function CategoriesCard({ categories, onChanged }) {
  const { notify, confirm } = useDialog();
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try { await api.post("/food-prep/categories", { name: name.trim() }); setName(""); onChanged?.(); }
    catch (e) { await notify({ title: "Couldn't add category", message: formatErr(e.response?.data?.detail), variant: "err" }); }
    finally { setAdding(false); }
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Delete category?", message: `Remove “${c.name}”? Existing presets keep their label.`, danger: true, confirmLabel: "Delete" }))) return;
    try { await api.delete(`/food-prep/categories/${c.id}`); onChanged?.(); }
    catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  return (
    <div className="vb-card p-5" data-testid="food-prep-categories">
      <h3 className="vb-heading text-lg mt-0 mb-3 flex items-center gap-2"><Tag size={16} /> Categories</h3>
      <p className="text-xs opacity-60 mb-3">Manage the category list chefs pick from when adding a preset. <span className="inline-flex items-center gap-1"><Lock size={10} /> Default</span> categories are set by Vanguard Beacon and apply to everyone — add your own below.</p>
      <form className="flex gap-2 mb-3" onSubmit={add}>
        <input className="vb-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sauces" data-testid="category-name" />
        <button className="vb-btn sm shrink-0" disabled={adding} data-testid="category-add"><Plus size={14} /> Add</button>
      </form>
      {categories.length === 0 ? (
        <div className="text-sm opacity-60">No categories yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="category-list">
          {categories.map((c) => (
            <span key={c.id} className="vb-pill" data-testid={`category-pill-${c.id}`} style={{ paddingRight: c.is_default ? undefined : 6 }}
              title={c.is_default ? "Default category set by Vanguard Beacon" : undefined}>
              {c.is_default && <Lock size={10} className="opacity-60 mr-0.5" />}
              {c.name}
              {!c.is_default && (
                <button type="button" className="ml-1 opacity-60 hover:opacity-100" onClick={() => remove(c)} data-testid={`category-delete-${c.id}`} aria-label={`Delete ${c.name}`}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsCard({ settings, onSaved }) {
  const { notify } = useDialog();
  const [retention, setRetention] = useState(settings.retention_months);
  const [amberHours, setAmberHours] = useState(Math.round((settings.default_amber_window_minutes || 0) / 60));
  const guardMins = settings.dispose_confirm_min_shelf_life_minutes || 0;
  const guardInit = guardMins > 0
    ? (guardMins % 1440 === 0 ? { value: guardMins / 1440, unit: "days" } : { value: Math.max(1, Math.round(guardMins / 60)), unit: "hours" })
    : { value: 1, unit: "days" };
  const [guardEnabled, setGuardEnabled] = useState(guardMins > 0);
  const [guardValue, setGuardValue] = useState(guardInit.value);
  const [guardUnit, setGuardUnit] = useState(guardInit.unit);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const gMins = guardEnabled ? Math.round(Number(guardValue) * (guardUnit === "days" ? 1440 : 60)) : 0;
    if (guardEnabled && (!gMins || gMins < 1)) {
      await notify({ title: "Invalid guard value", message: "Enter a shelf life of at least 1.", variant: "err" });
      return;
    }
    setSaving(true);
    try {
      await api.put("/food-prep/settings", {
        retention_months: Number(retention),
        default_amber_window_minutes: Math.round(Number(amberHours) * 60),
        dispose_confirm_min_shelf_life_minutes: gMins,
      });
      await notify({ title: "Saved", variant: "ok" });
      onSaved?.();
    } catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
    finally { setSaving(false); }
  };
  return (
    <div className="vb-card p-5" data-testid="food-prep-settings">
      <h3 className="vb-heading text-lg mt-0 mb-3 flex items-center gap-2"><SettingsIcon size={16} /> Settings</h3>
      <label className="block mb-3 text-sm">
        <span className="block mb-1 font-medium">Disposed-record retention (months)</span>
        <input className="vb-input" type="number" min="1" max="36" value={retention}
          onChange={(e) => setRetention(e.target.value)} data-testid="settings-retention" />
        <span className="text-xs opacity-60">1–36 months. Disposed records auto-purge after this.</span>
      </label>
      <label className="block mb-3 text-sm">
        <span className="block mb-1 font-medium">Default “expiring soon” window (Hours)</span>
        <input className="vb-input" type="number" min="0" value={amberHours}
          onChange={(e) => setAmberHours(e.target.value)} data-testid="settings-amber" />
        <span className="text-xs opacity-60">Items flag as “expiring soon” this many hours before their use-by time.</span>
      </label>

      <div className="mb-4 pt-3" style={{ borderTop: "1px solid var(--vb-border)" }}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldQuestion size={15} /><span className="font-medium text-sm">Dispose confirmation guard</span>
        </div>
        <p className="text-xs opacity-60 mb-2">In the Scanner's Active Tracking mode, scanned items that still have more shelf life than this minimum ask for confirmation before being disposed — protecting food that's still good.</p>
        <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer">
          <input type="checkbox" checked={guardEnabled} onChange={(e) => setGuardEnabled(e.target.checked)} data-testid="settings-guard-enabled" />
          Require confirmation above a minimum shelf life
        </label>
        {guardEnabled && (
          <div className="flex gap-2">
            <input className="vb-input" type="number" min="1" value={guardValue} style={{ maxWidth: 110 }}
              onChange={(e) => setGuardValue(e.target.value)} data-testid="settings-guard-value" />
            <select className="vb-input" value={guardUnit} onChange={(e) => setGuardUnit(e.target.value)} data-testid="settings-guard-unit">
              <option value="hours">hours</option><option value="days">days</option>
            </select>
          </div>
        )}
      </div>

      <button className="vb-btn w-full" disabled={saving} onClick={save} data-testid="settings-save">
        {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function PresetModal({ preset, categories = [], onClose, onSaved }) {
  const { notify } = useDialog();
  const isNew = !preset.id;
  const rawParts = preset.default_shelf_life_minutes ? minutesToParts(preset.default_shelf_life_minutes) : { value: 1, unit: "days" };
  const initParts = rawParts.unit === "minutes"
    ? { value: Math.max(1, Math.round(rawParts.value / 60)), unit: "hours" }
    : rawParts;
  const [name, setName] = useState(preset.name || "");
  const [category, setCategory] = useState(preset.category || "");
  const [value, setValue] = useState(initParts.value);
  const [unit, setUnit] = useState(initParts.unit);
  const [saving, setSaving] = useState(false);

  // Keep a legacy free-text category selectable even if it's not in the managed list.
  const catNames = categories.map((c) => c.name);
  const showLegacy = category && !catNames.includes(category);
  // Sort categories alphabetically but always push "Other"/"Others" to the bottom.
  const isOther = (nm) => /^others?$/i.test((nm || "").trim());
  const sortedCats = [...categories].sort((a, b) => {
    const ao = isOther(a.name) ? 1 : 0;
    const bo = isOther(b.name) ? 1 : 0;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  });

  const save = async (e) => {
    e?.preventDefault();
    const shelf = Math.round(Number(value) * UNIT_MULT[unit]);
    if (!name.trim() || !shelf) return;
    if (!category.trim()) {
      await notify({ title: "Category required", message: "Please choose a category for this item.", variant: "err" });
      return;
    }
    setSaving(true);
    const body = { name: name.trim(), category: category.trim(), default_shelf_life_minutes: shelf, amber_window_minutes: null };
    try {
      if (isNew) await api.post("/food-prep/presets", body);
      else await api.put(`/food-prep/presets/${preset.id}`, body);
      onSaved?.();
    } catch (e) { await notify({ title: "Failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
    finally { setSaving(false); }
  };

  return (
    <Portal>
    <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="preset-modal">
      <form className="vb-modal" style={{ maxWidth: 420 }} onSubmit={save}>
        <h2 className="text-lg">{isNew ? "Add preset" : "Edit preset"}</h2>
        <label className="block mb-3 text-sm">
          <span className="block mb-1 font-medium">Name</span>
          <input className="vb-input" value={name} required onChange={(e) => setName(e.target.value)} data-testid="preset-name" />
        </label>
        <label className="block mb-3 text-sm">
          <span className="block mb-1 font-medium">Category <span style={{ color: "var(--vb-error)" }}>*</span></span>
          <select className="vb-input" value={category} required onChange={(e) => setCategory(e.target.value)} data-testid="preset-category">
            <option value="" disabled>Select a category…</option>
            {sortedCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            {showLegacy && <option value={category}>{category}</option>}
          </select>
          {categories.length === 0 && <span className="text-xs opacity-60">Add categories under Food Catalog → Categories first.</span>}
        </label>
        <div className="mb-4">
          <span className="block mb-1 text-sm font-medium">Default shelf life</span>
          <div className="flex gap-2">
            <input className="vb-input" type="number" min="1" value={value} style={{ maxWidth: 110 }} onChange={(e) => setValue(e.target.value)} data-testid="preset-shelf-value" />
            <select className="vb-input" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="preset-shelf-unit">
              <option value="hours">hours</option><option value="days">days</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
          <button className="vb-btn" disabled={saving} data-testid="preset-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
    </Portal>
  );
}
